import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Factory, ShieldCheck, Package, Clock, ChevronRight, Wrench, Star, Award } from "lucide-react";
import { getCategories, getProducts, getPage, getProductsByIds } from "../lib/api";
import { mediaUrl } from "../lib/format";
import ProductCard from "../components/ProductCard";
import ProductImage from "../components/ProductImage";
import Blocks from "../components/Blocks";
import Seo from "../components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const ICONS = { Factory, Package, Truck, ShieldCheck, Clock, Wrench, Star, Award };

export default function Home() {
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [pickedFeatured, setPickedFeatured] = useState([]);
  const [sections, setSections] = useState([]);
  const [meta, setMeta] = useState({});

  useEffect(() => {
    const isPreview = new URLSearchParams(window.location.search).get("preview");
    getCategories().then((all) => setCats(all.filter((c) => !c.parent_id))).catch(() => {});
    getProducts({ featured: true, limit: 12 }).then((d) => setFeatured(d.items)).catch(() => {});
    if (!isPreview) getPage("home").then((p) => { setSections(p.sections || []); setMeta(p); }).catch(() => {});
  }, []);

  useEffect(() => {
    const isPreview = new URLSearchParams(window.location.search).get("preview");
    if (!isPreview) return;
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === "fp-preview-sections") setSections(e.data.sections || []);
    };
    window.addEventListener("message", onMsg);
    window.parent.postMessage({ type: "fp-preview-ready" }, window.location.origin);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    const f = sections.find((s) => s.type === "featured" && s.enabled);
    const ids = f && f.product_ids ? f.product_ids : [];
    if (ids.length) getProductsByIds(ids).then(setPickedFeatured).catch(() => {});
    else setPickedFeatured([]);
  }, [sections]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization", "@id": `${origin}/#organization`, name: "FABRIPAN",
        legalName: "DIRECTO DE FABRICA A LA PANADERIA S.L", url: origin, logo: `${origin}/logo.svg`,
        slogan: "Directo de fábrica a su panadería", telephone: "+34 931739860", email: "fabripan@fabripan.com",
        address: { "@type": "PostalAddress", streetAddress: "Av. Setze, 5, Urbanización la Florida",
          addressLocality: "Santa Perpetua de la Moguda", addressRegion: "Barcelona", postalCode: "08130", addressCountry: "ES" },
        contactPoint: { "@type": "ContactPoint", telephone: "+34 931739860", contactType: "sales", availableLanguage: ["es", "ca"] },
      },
      {
        "@type": "WebSite", "@id": `${origin}/#website`, url: origin, name: "FABRIPAN, Directo de fábrica a su panadería",
        publisher: { "@id": `${origin}/#organization` }, inLanguage: "es",
        potentialAction: { "@type": "SearchAction", target: `${origin}/tienda?q={search_term_string}`, "query-input": "required name=search_term_string" },
      },
    ],
  };

  const heroSection = sections.find((s) => s.type === "hero" && s.enabled);

  return (
    <div>
      <Seo
        title={meta.seo_title || ""}
        description={meta.meta_description || "FABRIPAN, directo de fábrica a su panadería. Fabricante de bandejas, armarios de transporte, carros bandejeros, moldes y equipamiento profesional para panaderías y pastelerías. Fabricación bajo pedido y envío a toda España."}
        keywords={meta.keywords}
        image={heroSection?.image}
        jsonLd={jsonLd}
      />

      {sections.filter((s) => s.enabled).map((s) => {
        if (s.type === "hero") return <Hero key={s.id} s={s} />;
        if (s.type === "advantages") return <Advantages key={s.id} s={s} />;
        if (s.type === "categories") return <Categories key={s.id} s={s} cats={cats} />;
        if (s.type === "featured") return <Featured key={s.id} s={s} featured={featured} picked={pickedFeatured} />;
        if (s.type === "richblocks") return (s.blocks && s.blocks.length > 0)
          ? <section key={s.id} className="max-w-7xl mx-auto px-4 py-14"><Blocks blocks={s.blocks} testid="home-blocks" /></section> : null;
        if (s.type === "process") return <Process key={s.id} s={s} />;
        if (s.type === "faq") return <Faq key={s.id} s={s} />;
        if (s.type === "cta") return <Cta key={s.id} s={s} />;
        return null;
      })}
    </div>
  );
}

const Hero = ({ s }) => (
  <section className="relative bg-brand-ink text-white overflow-hidden">
    <div className="absolute inset-0 hero-grain opacity-40" />
    <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-8 items-center py-16 lg:py-24 relative">
      <div className="animate-fadeup">
        {s.badge && <span className="inline-block font-mono text-xs text-brand border border-brand/40 px-3 py-1 rounded-sm mb-6">{s.badge}</span>}
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
          {s.title}{s.subtitle && <span className="block text-brand mt-2">{s.subtitle}</span>}
        </h1>
        {s.text && <p className="mt-6 text-white/70 text-base max-w-lg">{s.text}</p>}
        <div className="mt-8 flex flex-wrap gap-3">
          {s.cta1_label && <Link to={s.cta1_link || "/tienda"} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-7 py-3.5 rounded-full transition-colors" data-testid="hero-cta-products">{s.cta1_label} <ArrowRight className="h-4 w-4" /></Link>}
          {s.cta2_label && <Link to={s.cta2_link || "/contacto"} className="inline-flex items-center gap-2 border border-white/30 hover:border-brand hover:text-brand text-white font-semibold px-7 py-3.5 rounded-full transition-colors" data-testid="hero-cta-contact">{s.cta2_label}</Link>}
        </div>
      </div>
      {s.image && (
        <div className="relative animate-fadeup">
          <div className="aspect-[4/3] rounded-sm overflow-hidden border border-white/10">
            <ProductImage src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}
    </div>
  </section>
);

const Advantages = ({ s }) => (
  <section className="border-b border-border bg-white">
    <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
      {(s.items || []).map((a, i) => {
        const Icon = ICONS[a.icon] || Star;
        return (
          <div key={i} className="flex flex-col gap-2" data-testid={`advantage-${i}`}>
            <Icon className="h-7 w-7 text-brand" />
            <p className="font-heading font-semibold">{a.title}</p>
            <p className="text-sm text-muted-foreground">{a.text}</p>
          </div>
        );
      })}
    </div>
  </section>
);

const Categories = ({ s, cats }) => (
  <section className="max-w-7xl mx-auto px-4 py-14">
    <div className="flex items-end justify-between mb-6">
      <div>
        <h2 className="font-heading font-bold text-3xl tracking-tighter">{s.title}</h2>
        {s.subtitle && <p className="text-muted-foreground text-sm mt-1">{s.subtitle}</p>}
      </div>
      <Link to="/tienda" className="text-sm font-medium text-brand hover:underline flex items-center gap-1" data-testid="see-all-categories">Ver todo <ChevronRight className="h-4 w-4" /></Link>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cats.slice(0, s.limit || 8).map((c) => (
        <Link key={c.id} to={`/categoria/${c.slug}`} className="group relative bg-brand-ink text-white rounded-sm overflow-hidden p-5 h-32 flex flex-col justify-end hover:bg-brand transition-colors" data-testid={`home-cat-${c.slug}`}>
          <span className="font-heading font-semibold text-lg leading-tight uppercase">{c.name}</span>
          <span className="text-xs text-white/60 mt-1">{c.product_count} productos</span>
          <ArrowRight className="absolute top-4 right-4 h-5 w-5 text-white/40 group-hover:text-white transition-colors" />
        </Link>
      ))}
    </div>
  </section>
);

const Featured = ({ s, featured, picked }) => {
  const list = (s.product_ids && s.product_ids.length) ? (picked || []) : featured;
  return (
    <section className="bg-secondary/50 border-y border-border">
      <div className="max-w-7xl mx-auto px-4 py-14">
        <h2 className="font-heading font-bold text-3xl tracking-tighter mb-6">{s.title}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {list.slice(0, s.limit || 8).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </section>
  );
};

const Process = ({ s }) => (
  <section className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-10 items-center">
    {s.image && (
      <div className="aspect-[4/3] rounded-sm overflow-hidden border border-border">
        <ProductImage src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover" />
      </div>
    )}
    <div>
      <h2 className="font-heading font-bold text-3xl tracking-tighter">{s.title}</h2>
      {s.subtitle && <p className="text-muted-foreground mt-2">{s.subtitle}</p>}
      <ol className="mt-6 space-y-4">
        {(s.steps || []).map((step, i) => (
          <li key={i} className="flex gap-4 items-start" data-testid={`step-${i}`}>
            <span className="font-heading font-bold text-brand text-xl w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <p className="pt-1">{step}</p>
          </li>
        ))}
      </ol>
      {s.note && <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Clock className="h-4 w-4 text-brand" /> {s.note}</div>}
    </div>
  </section>
);

const Faq = ({ s }) => (
  <section className="bg-white border-t border-border">
    <div className="max-w-3xl mx-auto px-4 py-16">
      <h2 className="font-heading font-bold text-3xl tracking-tighter mb-6 text-center">{s.title}</h2>
      <Accordion type="single" collapsible className="w-full">
        {(s.items || []).map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
            <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
            <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

const Cta = ({ s }) => (
  <section className="bg-brand text-white">
    <div className="max-w-7xl mx-auto px-4 py-14 text-center">
      <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter">{s.title}</h2>
      {s.text && <p className="mt-3 text-white/80">{s.text}</p>}
      {s.button_label && <Link to={s.button_link || "/tienda"} className="inline-flex items-center gap-2 mt-6 bg-white text-brand font-bold px-8 py-3 rounded-sm hover:bg-white/90 transition-colors" data-testid="cta-shop">{s.button_label} <ArrowRight className="h-4 w-4" /></Link>}
    </div>
  </section>
);
